import { isLoading } from "apollo/utils";
import {
  FILE_CATEGORY_MR,
  FILE_CATEGORY_RBA,
  FILE_CATEGORY_TRC,
  FILE_ERROR_FILE_SIZE,
  FILE_ERROR_FILE_TYPE,
  FILE_SCOPE,
  FILE_SHARE_MODE_ALL,
  FILE_SHARE_MODE_NONE,
  FILE_SHARE_MODE_SELECTED,
  FILE_TABLE_ACTION_DELETE,
  RECARE_FILE_TYPE,
  REHAB_FORM_NAMES,
  REHAB_FORM_SUB_TYPES,
  REHAB_FORM_TYPES,
} from "core/consts";
import {
  ALLOWED_EXTENSIONS_HIS,
  BYTES_PER_MB,
  MAX_FILE_SIZE,
} from "core/model/files/config";
import { decryptFromSession } from "core/seald";
import {
  AllPatientForms,
  FileCategory,
  FileError,
  FileForm,
  FileFormType,
  FileHIS,
  FileState,
  FileTypeHIS,
  Filev2,
  Filev2Stateful,
  QueryProgress,
  RehabForm,
  RehabFormName,
  TransitionalCare,
} from "core/types";
import { FileName } from "ds_legacy/components/Tables/GeneralTable/CustomCells/Files";
import { GeneralListAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { isFileHISStateful } from "dsl/organisms/FileTables";
import { FileShareResultWithInitialValues } from "dsl/organisms/Modals/FileSharingModal";
import { ComponentProps } from "react";
import Translations from "translations/types";
import { FileFeatureData } from "./useFilesFeature";

export const getRehabFormTitle = ({
  form,
  translations,
}: {
  form: RehabForm;
  translations: Translations;
}): string => {
  const isGKV = form.rehab_form_subtype === REHAB_FORM_SUB_TYPES.GKV;
  const isGeneral = form.rehab_form_type === REHAB_FORM_TYPES.GENERAL;

  if (isGeneral) {
    return isGKV
      ? translations.patientForms.menu.rehabGeneralForm
      : translations.patientForms.menu.drvGeneralForm;
  }

  return isGKV
    ? translations.patientForms.menu.rehabMedicalForm
    : translations.patientForms.menu.drvMedicalForm;
};

export function getRehabFormName({
  form,
  translations,
}: {
  translations: Translations;
  form: RehabForm;
}): { formName: RehabFormName; formTitle: string } {
  const isGKV = form.rehab_form_subtype === REHAB_FORM_SUB_TYPES.GKV;
  const isGeneral = form.rehab_form_type === REHAB_FORM_TYPES.GENERAL;
  const formTitle = getRehabFormTitle({ form, translations });

  if (isGeneral) {
    return isGKV
      ? {
          formName: REHAB_FORM_NAMES.GKV_GENERAL,
          formTitle,
        }
      : {
          formName: REHAB_FORM_NAMES.DRV_GENERAL,
          formTitle,
        };
  }

  return isGKV
    ? {
        formName: REHAB_FORM_NAMES.GKV_MEDICAL,
        formTitle,
      }
    : {
        formName: REHAB_FORM_NAMES.DRV_MEDICAL,
        formTitle,
      };
}

export const isFileForm = (file: FileState | undefined): file is FileForm => {
  return (
    !!(file as Filev2)?.recare_file_type &&
    (file as Filev2).recare_file_type !== RECARE_FILE_TYPE.UNDEFINED &&
    !(file as Filev2).link
  );
};

export const isFileFormUploaded = (
  file: FileState | undefined,
): file is FileForm => {
  return (
    !!(file as Filev2)?.recare_file_type &&
    (file as Filev2).recare_file_type !== RECARE_FILE_TYPE.UNDEFINED
  );
};

export const isRehabForm = (
  form: RehabForm | TransitionalCare,
): form is RehabForm => {
  return !!(form as RehabForm).rehab_form_subtype;
};

export async function getFileNameAndIsNewVersionAvailable({
  canAccessPatientData,
  file,
}: {
  canAccessPatientData: boolean;
  file: FileState;
}): Promise<ComponentProps<typeof FileName>> {
  if (!canAccessPatientData) {
    return { shouldBlur: true, value: "blurred" };
  }

  if (isFileHISStateful(file) && !isFileForm(file)) {
    return { shouldBlur: false, value: file.document_file_name };
  }

  if (isFileForm(file) && !isFileHISStateful(file)) {
    return {
      shouldBlur: !file.file_name?.decrypted,
      value: file.file_name?.decrypted ?? "blurred",
    };
  }
  const value = await decryptFromSession({
    context: "file",
    encryptedMessage: file.file_name?.seald_content,
  });

  return {
    isFileDeleted: (file as Filev2Stateful).isFileDeleted
      ? (file as Filev2Stateful).isFileDeleted
      : false,
    isNewVersionAvailable: (file as Filev2Stateful).isNewVersionAvailable
      ? (file as Filev2Stateful).isNewVersionAvailable
      : false,
    shouldBlur: !value,
    updatedAt: (file as Filev2Stateful).updated_at ?? undefined,
    value: value ?? "blurred",
  };
}

export function getSharedWith({
  share_mode,
  translations,
}: Pick<FileShareResultWithInitialValues, "share_mode"> & {
  translations: Translations;
}): string {
  let result = "";
  if (share_mode === FILE_SHARE_MODE_NONE) {
    result = translations.fileSection.fileListComponent.shareOptions.noOne;
  }
  if (share_mode === FILE_SHARE_MODE_SELECTED) {
    result =
      translations.fileSection.fileListComponent.shareOptions.selectedReceivers;
  }
  if (share_mode === FILE_SHARE_MODE_ALL) {
    result =
      translations.fileSection.fileListComponent.shareOptions.allReceivers;
  }

  return result;
}

export function validateFileHIS({ fileHIS }: { fileHIS: FileHIS }): {
  isValid: boolean;
  type: FileTypeHIS | null;
  unavailable_reason: FileError | null;
} {
  const { document_file_name, document_file_size } = fileHIS;
  const fileParts = document_file_name?.split(".");
  const type = fileParts?.[fileParts.length - 1]?.toLowerCase() as FileTypeHIS;

  if (!document_file_name || !ALLOWED_EXTENSIONS_HIS.includes(type)) {
    return {
      isValid: false,
      type,
      unavailable_reason: FILE_ERROR_FILE_TYPE,
    };
  }

  if (!document_file_size || document_file_size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      type,
      unavailable_reason: FILE_ERROR_FILE_SIZE,
    };
  }

  return {
    isValid: true,
    type,
    unavailable_reason: null,
  };
}

export function isFileBeingDeleted({
  action,
  file,
  mutationProgress,
}: {
  action: GeneralListAction<FileFeatureData> | undefined;
  file: FileState;
  mutationProgress: QueryProgress;
}): boolean {
  if (!isLoading(mutationProgress)) return false;
  if (action?.type !== FILE_TABLE_ACTION_DELETE) return false;
  if (!action?.data) return false;
  if (Array.isArray(action.data)) return false;
  if (!("id" in action.data)) return false;

  return action.data.id === file.id;
}

export function getFileSize(file: FileState): number {
  let sizeInBytes = 0;

  if (isFileHISStateful(file)) {
    sizeInBytes = file.document_file_size || 0;
  } else {
    sizeInBytes = file.file_size ?? 0;
  }

  return Math.round((sizeInBytes / BYTES_PER_MB) * 100) / 100;
}

export const createFormFile = ({
  auctionId,
  category,
  fileType,
  form,
  translations,
}: {
  auctionId: number;
  category: FileCategory;
  form: RehabForm | TransitionalCare;
  fileType: FileFormType;
  translations: Translations;
}): FileForm => {
  const fileName = (() => {
    if (!isRehabForm(form)) {
      return translations.patientForms.menu.transitionalCare;
    }

    return getRehabFormTitle({ form, translations });
  })();

  return {
    __typename: "File",
    account: {
      __typename: "Account",
      first_name: "-",
      id: 0,
    },
    auction_id: auctionId,
    category,
    created_at: form.created_at ?? Date.now() / 1000, // TODO: Add actual created_at
    file_name: {
      content: null,
      decrypted: `${fileName}.pdf`,
      iv: null,
      seald_content: null,
    },
    file_scope: FILE_SCOPE.PATIENT_FILES,
    file_type: "application/pdf",
    id: form.id ?? 0,
    recare_file_type: fileType,
    share_mode: FILE_SHARE_MODE_NONE,
    shared_by: {
      __typename: "Account",
      first_name: "System",
      id: 0,
      last_name: "Generated",
    },
    updated_at: form.updated_at ?? Date.now() / 1000, // TODO: Add actual updated_at
  };
};

export function convertFormsToFiles({
  allFileForms,
  auctionId,
  translations,
}: {
  allFileForms: AllPatientForms;
  auctionId: number;
  translations: Translations;
}): FileForm[] | null {
  if (
    !allFileForms?.general_form &&
    !allFileForms?.medical_form &&
    !allFileForms?.transitional_care_form
  ) {
    return null;
  }

  const convertedFormFiles: FileForm[] = [];

  if (allFileForms.general_form) {
    convertedFormFiles.push(
      createFormFile({
        auctionId,
        category: FILE_CATEGORY_RBA,
        fileType:
          allFileForms.general_form.rehab_form_subtype ===
          REHAB_FORM_SUB_TYPES.GKV
            ? RECARE_FILE_TYPE.GKV_GENERAL_FORM
            : RECARE_FILE_TYPE.DRV_GENERAL_FORM,
        form: allFileForms.general_form,
        translations,
      }),
    );
  }

  if (allFileForms.medical_form) {
    convertedFormFiles.push(
      createFormFile({
        auctionId,
        category: FILE_CATEGORY_MR,
        fileType:
          allFileForms.medical_form.rehab_form_subtype ===
          REHAB_FORM_SUB_TYPES.GKV
            ? RECARE_FILE_TYPE.GKV_MEDICAL_FORM
            : RECARE_FILE_TYPE.DRV_MEDICAL_FORM,
        form: allFileForms.medical_form,
        translations,
      }),
    );
  }

  if (allFileForms.transitional_care_form) {
    convertedFormFiles.push(
      createFormFile({
        auctionId,
        category: FILE_CATEGORY_TRC,
        fileType: RECARE_FILE_TYPE.TRANSITIONAL_CARE,
        form: allFileForms.transitional_care_form,
        translations,
      }),
    );
  }

  return convertedFormFiles;
}
